import React, { useEffect, useRef, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';
import { Text } from '@chakra-ui/react';

// item 194 — reusable debounced auto-save for the standalone entity forms
// (Contact / User / Item / Project), which don't use DocumentFormScaffold. Drop it
// inside a react-final-form <Form> render, beside the Save button. Keeps the explicit
// Save as the official commit; this just fills the gaps as the user edits an EXISTING,
// editable record. Pair with `keepDirtyOnReinitialize` on the <Form> so the post-save
// refetch can't wipe edits typed during the save.
//
// item 1398 (Chad: "it will not let me change it … it just keeps trying to auto save") — THE
// RUNAWAY LOOP. The hosted log showed one contact being POSTed every 2 seconds for 90 minutes
// (2,248 saves), from a tab nobody was typing in, clobbering the email Chad typed in another
// tab. Mechanism: save → refetch → the form re-initialises → some field the SERVER normalises
// (a staff row that comes back with an id, a JSON blob re-serialised, a timestamp) no longer
// equals what we sent, so the form reads `dirty` and the snapshot differs → save again →
// forever. Same shape as the work-order runaway (1353b). The cure is the same: auto-save is
// gated on fields the USER actually modified since the last save (final-form's `modified`
// map), never on a diff the server produced — plus a circuit breaker.

type Badge = 'clean' | 'dirty' | 'saving' | 'saved' | 'error' | 'paused';

// item 557 — never silent: a clean form says "All changes saved" (people need to SEE
// that it's saved to trust it, matching the DocumentFormScaffold badge).
const LABELS: Record<Badge, { t: string; c: string }> = {
  clean: { t: 'All changes saved', c: 'green.400' },
  dirty: { t: 'Unsaved changes — auto-save runs when you pause', c: 'orange.400' },
  saving: { t: 'Auto-saving…', c: 'gray.400' },
  saved: { t: 'Auto-saved — all changes saved', c: 'green.400' },
  error: { t: 'Auto-save failed — use Save', c: 'red.400' },
  paused: { t: 'Auto-save paused (kept re-saving) — use Save', c: 'red.400' },
};

// item 1398 — more than this many auto-saves inside the window with no user edit between them
// is a loop, not a person; stop and hand control to the explicit Save button.
const BREAKER_MAX = 4;
const BREAKER_WINDOW_MS = 30_000;

interface Props {
  // only auto-save an existing, editable record (never a new/unsaved or locked one)
  enabled: boolean;
  // the form's save mutation, WITHOUT navigation (e.g. (v) => setContact(v))
  save: (values: any) => Promise<unknown>;
  // optional gate — skip the save while the record isn't valid enough to persist
  canSave?: (values: any) => boolean;
  debounceMs?: number;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const AutoSaveField: React.FC<Props> = ({
  enabled, save, canSave, debounceMs = 1500,
}) => {
  const [badge, setBadge] = useState<Badge>('clean');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const lastSavedRef = useRef<string | null>(null);
  // item 1398 — the VALUES we last saved, so a user edit is judged against what actually went
  // to the server (not against the server's normalised echo of it)
  const lastSavedValuesRef = useRef<Record<string, unknown> | null>(null);
  const latestRef = useRef<Record<string, unknown>>({});
  const modifiedRef = useRef<Record<string, boolean>>({});
  const pausedRef = useRef(false);
  const recentSavesRef = useRef<number[]>([]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // item 1398 — did the USER change anything since the last save? (fields final-form marks
  // `modified` whose value differs from what we last sent; before any save, any modified field)
  const userEditedSinceSave = (): boolean => {
    const modified = modifiedRef.current;
    const keys = Object.keys(modified).filter((k) => modified[k]);
    if (keys.length === 0) return false;
    if (lastSavedValuesRef.current === null) return true;
    return keys.some((k) => !same(getIn(latestRef.current, k), getIn(lastSavedValuesRef.current as object, k)));
  };

  const run = async () => {
    if (!enabled || savingRef.current || pausedRef.current) return;
    const snapshot = JSON.stringify(latestRef.current);
    if (snapshot === lastSavedRef.current) { setBadge('saved'); return; }
    if (!userEditedSinceSave()) { setBadge('saved'); return; } // server-side drift only — not ours to re-save
    if (canSave && !canSave(latestRef.current)) { setBadge('dirty'); return; }
    // circuit breaker — a person cannot produce this cadence
    const now = Date.now();
    recentSavesRef.current = recentSavesRef.current.filter((t) => now - t < BREAKER_WINDOW_MS);
    if (recentSavesRef.current.length >= BREAKER_MAX) {
      pausedRef.current = true;
      setBadge('paused');
      return;
    }
    recentSavesRef.current.push(now);
    savingRef.current = true;
    setBadge('saving');
    try {
      const sent = { ...latestRef.current };
      await save(sent);
      lastSavedRef.current = snapshot;
      lastSavedValuesRef.current = sent;
      setBadge('saved');
    } catch {
      setBadge('error'); // the explicit Save surfaces the detailed error toast
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <>
      <FormSpy
        subscription={{ values: true, dirty: true, modified: true }}
        onChange={(state) => {
          latestRef.current = state.values as Record<string, unknown>;
          modifiedRef.current = (state.modified ?? {}) as Record<string, boolean>;
          if (!enabled || pausedRef.current) return;
          const snapshot = JSON.stringify(state.values);
          if (!state.dirty || snapshot === lastSavedRef.current || !userEditedSinceSave()) {
            if (timer.current) { clearTimeout(timer.current); timer.current = null; }
            return;
          }
          setBadge('dirty');
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => { run(); }, debounceMs);
        }}
      />
      {enabled && (
        <Text fontSize="xs" color={LABELS[badge].c} fontWeight="medium" mr={2}>
          {LABELS[badge].t}
        </Text>
      )}
    </>
  );
};

AutoSaveField.defaultProps = {
  canSave: undefined,
  debounceMs: 1500,
};

export default AutoSaveField;
